import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { Send, Loader, Users, User, X } from "lucide-react";
import { lmsCreateNotice } from "../api/lmsApi";

const SCOPE_OPTIONS_BY_NOTICE_TYPE = {
  common: [
    { value: "class", label: "Class" },
    { value: "batch", label: "Batch (Course)" },
  ],
  direct: [
    { value: "student", label: "Student(s)" },
    { value: "batch", label: "Batch (Course)" },
  ],
};

const chipStyle = (active, disabled) => ({
  padding: "6px 14px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 600,
  border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
  background: active ? "#2563eb" : "#fff",
  color: active ? "#fff" : disabled ? "#cbd5e1" : "#334155",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const chipContainerStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "10px",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  maxHeight: "160px",
  overflowY: "auto",
};

// Add Notice popup — target type, target, title. Shared by the teacher and
// admin Notice Board pages; `createdByType`/`createdById` say who's sending
// it ("teacher" + teacherId, or "admin" + the school's admin id). Reuses
// the app's existing ms2-modal-overlay / crs-modal* form conventions (see
// Teachers.jsx).
//
// Two targets can go to more than one recipient at once, both flattened
// into a single scope_id string the backend already understands:
// - Common + Class: pick several classes, or "All Classes" -> "all" or a
//   comma-separated list of class ids.
// - Direct + Student(s): pick a class first to narrow the list, then
//   multi-select students from it -> a comma-separated list of student ids.
export default function AddNoticeModal({
  schoolId,
  createdByType,
  createdById,
  classes,
  courses,
  students,
  onClose,
  onCreated,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    notice_type: "common",
    scope_type: "class",
    scope_id: "",
    title: "",
  });
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [studentPickerClassId, setStudentPickerClassId] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  const isClassPicker = form.notice_type === "common" && form.scope_type === "class";
  const isStudentPicker = form.notice_type === "direct" && form.scope_type === "student";
  const scopeOptions = SCOPE_OPTIONS_BY_NOTICE_TYPE[form.notice_type];

  const scopeChoices = useMemo(() => {
    if (form.scope_type === "batch") {
      return courses.map((c) => ({ value: c.id, label: c.name }));
    }
    return [];
  }, [form.scope_type, courses]);

  const studentsInPickedClass = useMemo(() => {
    if (!studentPickerClassId) return [];
    return students.filter((s) => String(s.class_id) === String(studentPickerClassId));
  }, [students, studentPickerClassId]);

  const resetMultiSelects = () => {
    setSelectedClassIds([]);
    setStudentPickerClassId("");
    setSelectedStudentIds([]);
  };

  const handleNoticeTypeChange = (notice_type) => {
    const firstScope = SCOPE_OPTIONS_BY_NOTICE_TYPE[notice_type][0].value;
    setForm((prev) => ({ ...prev, notice_type, scope_type: firstScope, scope_id: "" }));
    resetMultiSelects();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "scope_type" ? { scope_id: "" } : {}),
    }));
    if (name === "scope_type") resetMultiSelects();
  };

  const toggleClass = (id) => {
    setSelectedClassIds((prev) => {
      if (id === "all") return prev.includes("all") ? [] : ["all"];
      const withoutAll = prev.filter((x) => x !== "all");
      return withoutAll.includes(id)
        ? withoutAll.filter((x) => x !== id)
        : [...withoutAll, id];
    });
  };

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!schoolId || !createdById) {
      toast.error("Could not determine your account");
      return;
    }

    let scope_id = form.scope_id;
    if (isClassPicker) {
      if (!selectedClassIds.length) {
        toast.error("Select at least one class, or All Classes");
        return;
      }
      scope_id = selectedClassIds.includes("all") ? "all" : selectedClassIds.join(",");
    } else if (isStudentPicker) {
      if (!studentPickerClassId) {
        toast.error("Select a class first");
        return;
      }
      if (!selectedStudentIds.length) {
        toast.error("Select at least one student");
        return;
      }
      scope_id = selectedStudentIds.join(",");
    } else if (!scope_id) {
      toast.error("Please select a target batch");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setSubmitting(true);
    try {
      const res = await lmsCreateNotice({
        school_id: schoolId,
        created_by_type: createdByType,
        created_by_id: createdById,
        notice_type: form.notice_type,
        scope_type: form.scope_type,
        scope_id,
        title: form.title,
      });

      toast.success(res.data?.message || "Notice sent successfully");
      onCreated();
      onClose();
    } catch (err) {
      console.error("createNotice error:", err);
      toast.error(err.response?.data?.message || "Failed to send notice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="crs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crs-modal-header">
          <span>Add Notice</span>
          <button className="ms2-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crs-modal-body">
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <button
                type="button"
                onClick={() => handleNoticeTypeChange("common")}
                className={form.notice_type === "common" ? "std-btn-primary" : "std-btn-cancel"}
                style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                <Users size={16} /> Common Notice Board
              </button>
              <button
                type="button"
                onClick={() => handleNoticeTypeChange("direct")}
                className={form.notice_type === "direct" ? "std-btn-primary" : "std-btn-cancel"}
                style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                <User size={16} /> One-to-One Communication
              </button>
            </div>

            <div
              className="crs-grid2"
              style={isClassPicker ? { gridTemplateColumns: "1fr" } : undefined}
            >
              <div className="crs-field">
                <label>
                  Target Type <span className="crs-req">*</span>
                </label>
                <select name="scope_type" value={form.scope_type} onChange={handleChange}>
                  {scopeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {form.scope_type === "batch" && (
                <div className="crs-field">
                  <label>
                    Select Batch <span className="crs-req">*</span>
                  </label>
                  <select name="scope_id" value={form.scope_id} onChange={handleChange}>
                    <option value="">-- Select --</option>
                    {scopeChoices.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isStudentPicker && (
                <div className="crs-field">
                  <label>
                    Select Class <span className="crs-req">*</span>
                  </label>
                  <select
                    value={studentPickerClassId}
                    onChange={(e) => {
                      setStudentPickerClassId(e.target.value);
                      setSelectedStudentIds([]);
                    }}
                  >
                    <option value="">-- Select --</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {isClassPicker && (
              <div className="crs-field">
                <label>
                  Select Classes <span className="crs-req">*</span>
                </label>
                <div style={chipContainerStyle}>
                  <button
                    type="button"
                    onClick={() => toggleClass("all")}
                    style={chipStyle(selectedClassIds.includes("all"), false)}
                  >
                    All Classes
                  </button>
                  {classes.map((c) => {
                    const disabled = selectedClassIds.includes("all");
                    return (
                      <button
                        type="button"
                        key={c.id}
                        disabled={disabled}
                        onClick={() => toggleClass(c.id)}
                        style={chipStyle(selectedClassIds.includes(c.id), disabled)}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isStudentPicker && (
              <div className="crs-field">
                <label>
                  Select Students <span className="crs-req">*</span>
                </label>
                {!studentPickerClassId ? (
                  <div style={{ fontSize: "13px", color: "#94a3b8", padding: "8px 0" }}>
                    Select a class first.
                  </div>
                ) : studentsInPickedClass.length === 0 ? (
                  <div style={{ fontSize: "13px", color: "#94a3b8", padding: "8px 0" }}>
                    No students in this class.
                  </div>
                ) : (
                  <div style={chipContainerStyle}>
                    {studentsInPickedClass.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => toggleStudent(s.id)}
                        style={chipStyle(selectedStudentIds.includes(s.id), false)}
                      >
                        {`${s.first_name || ""} ${s.last_name || ""}`.trim() || `Student #${s.id}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="crs-field">
              <label>
                Title <span className="crs-req">*</span>
              </label>
              <input
                type="text"
                name="title"
                placeholder="e.g. Holiday Notice"
                value={form.title}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="crs-modal-footer">
            <button type="button" className="crs-btn-cancel" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="crs-btn-save" disabled={submitting}>
              {submitting ? <Loader size={13} className="ms2-spin" /> : <Send size={13} />}
              {submitting ? "Sending..." : "Send Notice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
